import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, Plus, Trash2, Loader2, Download, ArrowLeft, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import BrandLogo from '../../components/BrandLogo';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/ui/toaster';
import EmailGate, { useEmailGate } from '../../components/EmailGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIRLINE_LOGO = (code) =>
  `https://images.kiwi.com/airlines/64/${(code || '').toUpperCase()}.png`;

const emptySegment = () => ({
  origin: '',
  destination: '',
  date: '',
  airline: '',
  class_of_service: 'Economy',
});

export default function FlightItineraryPage() {
  const navigate = useNavigate();
  const gate = useEmailGate('flight-itinerary');
  const [passenger, setPassenger] = useState('');
  const [tripName, setTripName] = useState('');
  const [segments, setSegments] = useState([
    { origin: 'DEL', destination: 'DXB', date: '2026-08-15', airline: 'EK', class_of_service: 'Economy' },
    { origin: 'DXB', destination: 'JFK', date: '2026-08-15', airline: 'EK', class_of_service: 'Economy' },
  ]);
  const [preferences, setPreferences] = useState('Window seats, vegetarian meals, extra legroom');
  const [loading, setLoading] = useState(false);
  const [itin, setItin] = useState(null);
  const pdfRef = useRef(null);

  const updateSeg = (i, key, val) => {
    const next = [...segments];
    next[i] = { ...next[i], [key]: val };
    setSegments(next);
  };

  const addSeg = () => setSegments([...segments, emptySegment()]);
  const removeSeg = (i) => setSegments(segments.filter((_, idx) => idx !== i));

  const generate = async () => {
    if (!passenger.trim()) {
      toast({ title: 'Passenger name required', description: 'Please add a passenger name.' });
      return;
    }
    if (segments.some((s) => !s.origin || !s.destination)) {
      toast({ title: 'Missing route', description: 'Fill origin + destination for every segment.' });
      return;
    }
    setLoading(true);
    setItin(null);
    try {
      const r = await fetch(`${API}/templates/flight-itinerary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trip_name: tripName || undefined,
          passenger_name: passenger,
          segments,
          preferences: preferences || undefined,
        }),
      });
      if (!r.ok) throw new Error(`Server error (${r.status})`);
      const data = await r.json();
      setItin(data);
    } catch (e) {
      toast({ title: 'Generation failed', description: e.message });
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    gate.run(async () => {
      if (!pdfRef.current) return;
      toast({ title: 'Preparing PDF…' });
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        backgroundColor: '#0b1120',
        useCORS: true,
      });
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = (canvas.height * pdfW) / canvas.width;
      pdf.addImage(img, 'PNG', 0, 0, pdfW, pdfH, undefined, 'FAST');
      pdf.save(`${(itin?.title || 'itinerary').replace(/\s+/g, '_')}.pdf`);
      toast({ title: 'PDF downloaded ✅' });
    });
  };

  return (
    <div className="min-h-screen bg-[#050818] text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center gap-4">
          <button
            data-testid="flight-back"
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white flex items-center gap-2 text-sm"
          >
            <ArrowLeft size={16} /> Home
          </button>
          <span className="text-white/25">/</span>
          <span className="text-sm text-white/80">Templates</span>
          <span className="text-white/25">/</span>
          <span className="text-sm text-cyan-300 font-semibold">Flight Itinerary</span>
          <div className="ml-auto">
            <BrandLogo size={24} tone="light" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[400px_1fr] gap-8">
        {/* LEFT: form */}
        <div className="space-y-5">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/15 mb-4">
              <Plane size={13} className="text-cyan-300" />
              <span className="text-[11px] tracking-widest uppercase font-bold text-white/85">
                Flight Itinerary
              </span>
            </div>
            <h1 className="text-3xl font-black tracking-tight">Build your itinerary</h1>
            <p className="text-sm text-white/60 mt-1.5">
              Fill in your route(s). We&rsquo;ll generate a real-looking PDF with airline logos.
            </p>
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Trip name (optional)</label>
            <input
              data-testid="flight-trip-name"
              value={tripName}
              onChange={(e) => setTripName(e.target.value)}
              placeholder="European Adventure"
              className="mt-2 w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-cyan-400 outline-none text-sm"
            />
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Passenger name *</label>
            <input
              data-testid="flight-passenger"
              value={passenger}
              onChange={(e) => setPassenger(e.target.value)}
              placeholder="John Doe"
              className="mt-2 w-full h-11 px-3 rounded-xl bg-white/5 border border-white/15 focus:border-cyan-400 outline-none text-sm"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Segments</label>
              <button
                data-testid="flight-add-segment"
                onClick={addSeg}
                className="text-cyan-300 text-xs font-bold flex items-center gap-1 hover:text-cyan-200"
              >
                <Plus size={12} /> Add
              </button>
            </div>
            {segments.map((s, i) => (
              <div
                key={i}
                data-testid={`flight-segment-${i}`}
                className="p-4 rounded-xl bg-white/4 border border-white/10 space-y-2"
              >
                <div className="flex items-center justify-between text-[10px] tracking-widest uppercase text-white/50 font-bold">
                  Leg {i + 1}
                  {segments.length > 1 && (
                    <button onClick={() => removeSeg(i)} className="text-rose-300 hover:text-rose-200">
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={s.origin}
                    onChange={(e) => updateSeg(i, 'origin', e.target.value.toUpperCase())}
                    placeholder="From (DEL)"
                    className="h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-cyan-400 outline-none text-sm font-mono uppercase"
                  />
                  <input
                    value={s.destination}
                    onChange={(e) => updateSeg(i, 'destination', e.target.value.toUpperCase())}
                    placeholder="To (DXB)"
                    className="h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-cyan-400 outline-none text-sm font-mono uppercase"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={s.date}
                    onChange={(e) => updateSeg(i, 'date', e.target.value)}
                    className="h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-cyan-400 outline-none text-sm"
                  />
                  <input
                    value={s.airline}
                    onChange={(e) => updateSeg(i, 'airline', e.target.value.toUpperCase())}
                    placeholder="Airline (EK)"
                    className="h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-cyan-400 outline-none text-sm font-mono uppercase"
                  />
                </div>
                <select
                  value={s.class_of_service}
                  onChange={(e) => updateSeg(i, 'class_of_service', e.target.value)}
                  className="w-full h-9 px-2.5 rounded-lg bg-black/30 border border-white/10 focus:border-cyan-400 outline-none text-sm"
                >
                  <option>Economy</option>
                  <option>Premium Economy</option>
                  <option>Business</option>
                  <option>First</option>
                </select>
              </div>
            ))}
          </div>

          <div>
            <label className="text-[11px] tracking-widest uppercase text-white/60 font-bold">Preferences (optional)</label>
            <textarea
              value={preferences}
              onChange={(e) => setPreferences(e.target.value)}
              rows={2}
              className="mt-2 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/15 focus:border-cyan-400 outline-none text-sm"
            />
          </div>

          <Button
            data-testid="flight-generate"
            onClick={generate}
            disabled={loading}
            className="w-full h-12 rounded-xl bg-gradient-to-r from-cyan-400 via-indigo-500 to-fuchsia-500 text-white font-bold shadow-[0_20px_50px_-20px_rgba(99,102,241,0.6)]"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={16} /> Generating…
              </>
            ) : (
              <>
                <Sparkles className="mr-2" size={16} /> Generate itinerary
              </>
            )}
          </Button>
        </div>

        {/* RIGHT: Result */}
        <div>
          {!itin && !loading && (
            <div className="border-2 border-dashed border-white/10 rounded-2xl p-16 text-center text-white/50">
              <Plane size={40} className="mx-auto text-white/25 mb-4" />
              <div className="font-bold text-white/80">Your itinerary will appear here</div>
              <div className="text-sm mt-2">Fill in your details and hit Generate.</div>
            </div>
          )}

          {loading && (
            <div className="border-2 border-dashed border-cyan-400/30 rounded-2xl p-16 text-center">
              <Loader2 size={40} className="mx-auto text-cyan-300 mb-4 animate-spin" />
              <div className="font-bold">Crafting your itinerary…</div>
              <div className="text-sm text-white/60 mt-2">Booking gates, seats and meals with Claude 4.5</div>
            </div>
          )}

          {itin && (
            <div>
              <div className="flex justify-end mb-4">
                <Button
                  data-testid="flight-download-pdf"
                  onClick={downloadPDF}
                  className="rounded-xl bg-white text-slate-900 font-bold h-11"
                >
                  <Download size={16} className="mr-2" /> Download PDF
                </Button>
              </div>
              <ItineraryDoc data={itin} innerRef={pdfRef} />
            </div>
          )}
        </div>
      </div>
      <Toaster />
      <EmailGate {...gate} />
    </div>
  );
}

function ItineraryDoc({ data, innerRef }) {
  return (
    <div
      ref={innerRef}
      className="rounded-2xl overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0b1120 0%, #060a1a 100%)',
        boxShadow: '0 30px 80px -30px rgba(0,0,0,0.8)',
      }}
    >
      {/* Cover */}
      <div
        className="p-8 border-b border-white/10"
        style={{
          background:
            'linear-gradient(135deg, rgba(6,182,212,0.35), rgba(99,102,241,0.35) 50%, rgba(192,38,211,0.3))',
        }}
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-100 font-bold mb-2">
              Around the World AI &middot; Booking Confirmation
            </div>
            <div className="text-3xl md:text-4xl font-black tracking-tight text-white">
              {data.title}
            </div>
            <div className="mt-2 text-white/85 text-sm">
              Passenger: <span className="font-bold">{data.passenger}</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-widest text-white/70 uppercase font-bold">PNR</div>
            <div className="text-2xl font-black font-mono text-white">{data.pnr}</div>
          </div>
        </div>
      </div>

      {/* Segments */}
      <div className="p-8 space-y-5">
        {(data.segments || []).map((seg, i) => (
          <div
            key={i}
            className="rounded-xl border border-white/10 overflow-hidden"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(0,0,0,0.15))' }}
          >
            <div className="flex items-center gap-3 px-5 py-3 border-b border-white/8 bg-black/25">
              <img
                src={AIRLINE_LOGO(seg.airline_code)}
                alt={seg.airline_name}
                className="h-8 w-8 rounded bg-white/95 p-1"
                onError={(e) => (e.target.style.display = 'none')}
                crossOrigin="anonymous"
              />
              <div className="flex-1">
                <div className="font-bold text-white text-sm">{seg.airline_name}</div>
                <div className="text-[11px] text-white/60 font-mono">{seg.flight_number}</div>
              </div>
              <div className="text-right text-[11px] text-white/60">
                <div>{seg.cabin}</div>
                <div className="font-mono">Seat {seg.seat}</div>
              </div>
            </div>

            <div className="p-5 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
              <div>
                <div className="text-4xl font-black tracking-tighter text-white">
                  {seg.from?.code}
                </div>
                <div className="text-sm font-bold text-cyan-300 mt-0.5">{seg.from?.city}</div>
                <div className="text-[11px] text-white/55 mt-1">{seg.from?.airport}</div>
                {seg.from?.terminal && (
                  <div className="text-[10px] text-white/45 mt-1 uppercase tracking-widest font-bold">
                    Terminal {seg.from.terminal}
                  </div>
                )}
                <div className="mt-3 text-xs text-white/85">
                  {fmtDate(seg.depart_local)} <span className="font-bold ml-1">{fmtTime(seg.depart_local)}</span>
                </div>
              </div>

              <div className="flex flex-col items-center gap-1 opacity-70">
                <div className="text-[9px] uppercase tracking-widest text-white/50 font-bold">
                  {seg.duration}
                </div>
                <div className="relative flex items-center">
                  <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />
                  <span className="w-16 h-px bg-gradient-to-r from-cyan-300 to-fuchsia-400" />
                  <Plane size={14} className="text-fuchsia-300 -mx-1" />
                  <span className="w-16 h-px bg-gradient-to-r from-fuchsia-400 to-cyan-300" />
                  <span className="h-1.5 w-1.5 rounded-full bg-fuchsia-300" />
                </div>
                <div className="text-[9px] uppercase tracking-widest text-white/40 font-bold">
                  Direct
                </div>
              </div>

              <div className="text-right">
                <div className="text-4xl font-black tracking-tighter text-white">
                  {seg.to?.code}
                </div>
                <div className="text-sm font-bold text-fuchsia-300 mt-0.5">{seg.to?.city}</div>
                <div className="text-[11px] text-white/55 mt-1">{seg.to?.airport}</div>
                {seg.to?.terminal && (
                  <div className="text-[10px] text-white/45 mt-1 uppercase tracking-widest font-bold">
                    Terminal {seg.to.terminal}
                  </div>
                )}
                <div className="mt-3 text-xs text-white/85">
                  {fmtDate(seg.arrive_local)} <span className="font-bold ml-1">{fmtTime(seg.arrive_local)}</span>
                </div>
              </div>
            </div>

            <div className="px-5 py-3 border-t border-white/8 bg-black/15 grid grid-cols-3 gap-2 text-[10px] uppercase tracking-widest">
              <div>
                <div className="text-white/40 font-bold">Meal</div>
                <div className="text-white/85 mt-0.5">{seg.meal || 'Standard'}</div>
              </div>
              <div>
                <div className="text-white/40 font-bold">Baggage</div>
                <div className="text-white/85 mt-0.5">{seg.baggage || '20kg'}</div>
              </div>
              <div>
                <div className="text-white/40 font-bold">Flight time</div>
                <div className="text-white/85 mt-0.5">{seg.duration}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {data.notes && (
        <div className="px-8 pb-8">
          <div className="rounded-xl border border-amber-400/25 bg-amber-500/8 px-4 py-3">
            <div className="text-[10px] tracking-widest uppercase text-amber-300 font-bold mb-1">
              Important
            </div>
            <div className="text-sm text-white/85 leading-relaxed">{data.notes}</div>
          </div>
        </div>
      )}

      <div className="px-8 pb-8 flex items-center justify-between text-[10px] uppercase tracking-widest text-white/40 font-bold">
        <div>Powered by Around the World AI</div>
        <div>aroundtheworld.ai</div>
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}
function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
